import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input and manage search type dropdown.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @property {HTMLButtonElement} [searchDropdownTrigger] - The dropdown trigger button.
 * @property {HTMLElement} [searchDropdownMenu] - The dropdown menu container.
 * @property {HTMLInputElement} [searchTypeInput] - The hidden input for search type.
 * @property {HTMLElement[]} [searchDropdownOptions] - The dropdown option elements.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  #isDropdownOpen = false;
  #dropdownEventListeners = [];

  /**
   * Handles the click event on the clear button.
   * This clears the search input without submitting the form.
   */
  handleClearClick() {
    const searchInput = this.refs.searchPageInput;
    searchInput.value = '';
    searchInput.focus();
  }

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.handleClearClick();
    }
  }, 100);

  /**
   * Initializes the dropdown functionality if dropdown elements are present.
   */
  connectedCallback() {
    super.connectedCallback();

    if (this.refs.searchDropdownTrigger && this.refs.searchDropdownMenu) {
      this.#initializeDropdown();
    }
  }

  /**
   * Cleans up event listeners when component is disconnected.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupDropdown();
  }

  /**
   * Initializes dropdown state and event listeners.
   */
  #initializeDropdown() {
    const { searchDropdownTrigger, searchDropdownMenu, searchTypeInput, searchDropdownOptions } = this.refs;

    if (!searchDropdownTrigger || !searchDropdownMenu || !searchTypeInput) return;

    this.#syncWithURL();

    const toggleHandler = (e) => {
      e.stopPropagation();
      this.#toggleDropdown();
    };
    searchDropdownTrigger.addEventListener('click', toggleHandler);
    this.#dropdownEventListeners.push({ element: searchDropdownTrigger, event: 'click', handler: toggleHandler });

    if (searchDropdownOptions && searchDropdownOptions.length > 0) {
      searchDropdownOptions.forEach((option) => {
        const optionHandler = (e) => {
          e.stopPropagation();
          this.#handleOptionClick(option);
        };
        option.addEventListener('click', optionHandler);
        this.#dropdownEventListeners.push({ element: option, event: 'click', handler: optionHandler });
      });
    }

    const outsideClickHandler = (e) => {
      if (this.#isDropdownOpen && !searchDropdownTrigger.contains(e.target) && !searchDropdownMenu.contains(e.target)) {
        this.#closeDropdown();
      }
    };
    document.addEventListener('click', outsideClickHandler);
    this.#dropdownEventListeners.push({ element: document, event: 'click', handler: outsideClickHandler });

    const escapeHandler = (e) => {
      if (e.key === 'Escape' && this.#isDropdownOpen) {
        this.#closeDropdown();
      }
    };
    document.addEventListener('keydown', escapeHandler);
    this.#dropdownEventListeners.push({ element: document, event: 'keydown', handler: escapeHandler });
  }

  /**
   * Cleans up all dropdown event listeners.
   */
  #cleanupDropdown() {
    this.#dropdownEventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.#dropdownEventListeners = [];
  }

  /**
   * Updates the selected option UI and hidden input value.
   *
   * @param {string} searchType - The search type ('product' or 'page,article').
   */
  #updateSelectedOption(searchType) {
    const { searchDropdownOptions, searchTypeInput, searchDropdownTrigger } = this.refs;
    if (!searchDropdownOptions || !searchTypeInput || !searchDropdownTrigger) return;

    const triggerText = searchDropdownTrigger.querySelector('.search-dropdown__trigger-text');
    if (!triggerText) return;

    searchDropdownOptions.forEach((option) => {
      const optionType = option.getAttribute('data-search-type');

      if (optionType === searchType) {
        option.classList.add('search-dropdown__option--selected');
      } else {
        option.classList.remove('search-dropdown__option--selected');
      }
    });

    const selectedOption = Array.from(searchDropdownOptions).find(
      (opt) => opt.getAttribute('data-search-type') === searchType
    );

    if (selectedOption) {
      const label = selectedOption.querySelector('.search-dropdown__option-label');

      if (label) {
        triggerText.textContent = label.textContent.trim();
      }
    }

    searchTypeInput.value = searchType;
  }

  /**
   * Syncs dropdown state with URL parameters.
   */
  #syncWithURL() {
    const { searchTypeInput } = this.refs;
    if (!searchTypeInput) return;

    const urlParams = new URLSearchParams(window.location.search);
    const urlType = urlParams.get('type') || 'product';
    const currentType = searchTypeInput.value;

    if (urlType !== currentType) {
      const isPagesBlogs = urlType === 'page,article' || urlType === 'article' || urlType === 'page';
      this.#updateSelectedOption(isPagesBlogs ? 'page,article' : 'product');
    }
  }

  /**
   * Handles option click in the dropdown.
   *
   * @param {HTMLElement} option - The clicked option element.
   */
  #handleOptionClick(option) {
    const { searchTypeInput } = this.refs;
    if (!searchTypeInput) return;

    const searchType = option.getAttribute('data-search-type');
    if (!searchType) return;

    const currentType = searchTypeInput.value;
    if (searchType === currentType) {
      this.#closeDropdown();
      return;
    }

    const form = searchTypeInput.form;
    if (!form) return;

    this.#updateSelectedOption(searchType);
    this.#showSkeletonLoader();
    this.#closeDropdown();

    form.submit();
  }

  /**
   * Toggles the dropdown open/closed state.
   */
  #toggleDropdown() {
    this.#isDropdownOpen = !this.#isDropdownOpen;
    this.#updateDropdownState();
  }

  /**
   * Closes the dropdown.
   */
  #closeDropdown() {
    this.#isDropdownOpen = false;
    this.#updateDropdownState();
  }

  /**
   * Updates the dropdown's visual state and ARIA attributes.
   */
  #updateDropdownState() {
    const { searchDropdownTrigger, searchDropdownMenu } = this.refs;
    if (!searchDropdownTrigger || !searchDropdownMenu) return;

    if (this.#isDropdownOpen) {
      searchDropdownMenu.classList.remove('popup--hidden');
      searchDropdownMenu.setAttribute('aria-hidden', 'false');
      searchDropdownTrigger.setAttribute('aria-expanded', 'true');
    } else {
      searchDropdownMenu.classList.add('popup--hidden');
      searchDropdownMenu.setAttribute('aria-hidden', 'true');
      searchDropdownTrigger.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Shows skeleton loader in product grid when switching search types.
   */
  #showSkeletonLoader() {
    const productGrid =
      document.querySelector('ul.product-grid') ||
      document.querySelector('#searchspring-product-grid') ||
      document.querySelector('ul.search-results__mixed-grid');

    if (!productGrid) return;

    const skeletonCard = `
      <li class="product-grid__item">
        <div class="searchspring-skeleton-card">
          <div class="searchspring-skeleton-card__image skeleton-item"></div>
          <div class="searchspring-skeleton-card__title skeleton-item"></div>
          <div class="searchspring-skeleton-card__price skeleton-item"></div>
          <div class="searchspring-skeleton-card__swatches">
            <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
            <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
            <div class="searchspring-skeleton-card__swatch skeleton-item"></div>
          </div>
        </div>
      </li>
    `;

    const skeletonHTML = skeletonCard.repeat(8);
    productGrid.innerHTML = skeletonHTML;
    productGrid.classList.add('searchspring-skeleton-grid');
  }
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
